import { IconButton, makeStyles, Slider, withStyles } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import cx from 'classnames';
import EyeIcon from 'components/base/icons/EyeIcon';
import { btn_opacity, icon_folder_plus, icon_mesh_off, icon_mesh_on } from 'components/base/images';
import CircularLoading from 'components/base/loading/CircularLoading';
import CustomCheckbox from 'components/common/checkbox/CustomCheckbox';
import DropzoneWrapper from 'components/common/dropzone/DropzoneWrapper';
import T from 'components/common/text/T';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { color } from 'styles/utils';
import ProjectFileIcon from './ProjectFileIcon';

const imgTypes = ['png', 'jpg', 'jpeg', 'gif'];
const modelTypes = ['obj', 'ply', 'stl'];

interface ProjectFileGroupLocalProps {
  projectId: string;
  hasAlarm: any;
  hasViewer: boolean;
  fileList: any;
  title: string;
  localFileInputRef: React.MutableRefObject<any>;
  localFileInput: {
    value: any;
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
    reset: () => void;
  };
  onClickFileImport: () => void;
  onAddLocalFiles: (data: any) => void;
  onClickWireframe: (id: number, data: any) => void;
  onChangeModelOpacity: (e: any, id: number, newValue: number | number[]) => void;
  isModelFileLoadingProgress: {
    value: boolean;
    onChange: (e: boolean) => void;
    setValue: React.Dispatch<any>;
  };
  selectedModelFile: any;
  onClickViewModelFileLocal: (e: any, id: any, data?: any) => Promise<any>;
  onClickImageFileLocal: (e: any, id: any, data?: any) => Promise<any>;
  onClickCheckbox: (e: any, id: any, storage: string, data: any) => void;
  onClickCheckboxAll: (e: any, storage: string, isCheckAll: boolean, shareType: string) => void;
  onClickDeleteFiles: (storage: string, deletelist: any[]) => void;
}
function ProjectFileGroupLocal({
  projectId,
  hasAlarm,
  hasViewer,
  fileList,
  title,
  localFileInputRef,
  localFileInput,
  isModelFileLoadingProgress,
  selectedModelFile,
  onClickFileImport,
  onAddLocalFiles,
  onClickWireframe,
  onChangeModelOpacity,
  onClickViewModelFileLocal,
  onClickImageFileLocal,
  onClickCheckbox,
  onClickCheckboxAll,
  onClickDeleteFiles,
}: ProjectFileGroupLocalProps) {
  const [isCheckAll, setIsCheckAll] = useState(false);
  const [checkedList, setCheckedList] = useState<number[]>([]);

  useEffect(() => {
    if (fileList.value.length === 0) {
      setIsCheckAll(false);
      return;
    }
    let checkedCount = 0;
    fileList.value.forEach((item: any) => {
      if (item.isChecked) {
        checkedCount++;
      }
    });

    if (fileList.value.length === checkedCount) {
      setIsCheckAll(true);
    } else {
      setIsCheckAll(false);
    }
  }, [fileList.value]);

  const checkboxOnClickAll = (e: any) => {
    setIsCheckAll(!isCheckAll);
    onClickCheckboxAll(_, 'local', isCheckAll, '');
  };

  const handleClickDelete = () => {
    const deleteIdList = checkedList.map((item: any) => {
      return item.name;
    });

    onClickDeleteFiles('local', deleteIdList);
  };

  useEffect(() => {
    const checkedList = fileList.value.filter((item: any) => {
      return !!item.isChecked;
    });
    setCheckedList([...checkedList]);
  }, [fileList.value]);

  return (
    <StyledProjectFileGroupLocal data-component-name="projectFileGroupLocal">
      <div className="projectFileGroupLocal__header">
        <div className="projectFileGroupLocal__title_box">
          {useMemo(
            () => (
              <label
                className={cx('checkBox', {
                  isHover: fileList.value?.length > 0,
                })}
              >
                <CustomCheckbox
                  marginRight={0}
                  width={15}
                  disabled={fileList.value.length === 0}
                  checked={!!isCheckAll}
                  onClick={e => {
                    checkboxOnClickAll(e);
                  }}
                />
              </label>
            ),
            [isCheckAll, fileList.value],
          )}
          <div className="projectFileGroupLocal__title">{title}</div>
        </div>

        <div className="projectFileGroupLocal__button_box">
          <IconButton
            className={cx('projectFileGroupLocal__button', {
              on: checkedList.length > 0,
            })}
            onClick={handleClickDelete}
            disabled={checkedList.length === 0}
          >
            <T>GLOBAL_DELETE</T>
          </IconButton>
        </div>
      </div>
      <div className="projectFileGroupLocal__body">
        <DropzoneWrapper
          apiRequest={e => {
            if (!!hasAlarm) return;
            onAddLocalFiles(e);
          }}
        >
          <div className="projectFileGroupLocal__file_list">
            {useMemo(
              () => (
                <>
                  {fileList.value?.length > 0 &&
                    fileList.value.map((item: any, idx: number) => {
                      const isModelType = modelTypes.includes(item.type);
                      const isImageType = [...imgTypes].includes(item.type);
                      const isViewType = isModelType || isImageType || item.type === 'html';

                      const activeEyeColor = item.viewColor || color.blue;
                      const unactiveEyeColor = isViewType ? color.gray_b5 : color.gray_deep_weak;
                      const eyeIconColor = item.isView ? activeEyeColor : unactiveEyeColor;
                      return (
                        <div
                          className={cx('projectFileGroupLocal__file_row', {
                            isDisplayed: true,
                          })}
                          key={idx}
                        >
                          <div className="projectFileGroupLocal__file_row_cell check_item">
                            <label className="isHover">
                              {!item.uploadState ? (
                                <CustomCheckbox
                                  marginRight={0}
                                  width={15}
                                  disabled={item.uploadState}
                                  checked={item.isChecked}
                                  onChange={e => {
                                    onClickCheckbox(_, item.id, 'local', {
                                      isChecked: item.isChecked,
                                    });
                                  }}
                                />
                              ) : (
                                <IconButton disabled style={{ paddingTop: 0, paddingBottom: 0 }}>
                                  <CircularLoading size={15} />
                                </IconButton>
                              )}
                            </label>
                          </div>
                          <div className="projectFileGroupLocal__file_row_cell check_item">
                            <ProjectFileIcon fileName={item.name} projectId={projectId} />
                          </div>

                          <div className="projectFileGroupLocal__file_row_cell filename">
                            <span
                              className="projectFileGroupLocal__file_list_item_text text-overflow-ellipsis"
                              title={item.name}
                            >
                              {item.name}
                            </span>
                            {!!isModelType && (
                              <div className="slider_box">
                                <FileSlider
                                  disabled={!item.isView || !!isModelFileLoadingProgress.value}
                                  defaultValue={100}
                                  aria-label="Small"
                                  valueLabelDisplay="auto"
                                  onChange={(e, newVal) => {
                                    onChangeModelOpacity(e, item.id, newVal);
                                  }}
                                  value={item.opacity}
                                  ValueLabelComponent={ValueLabelComponent}
                                />
                              </div>
                            )}
                          </div>
                          <div className="projectFileGroupLocal__file_row_cell actions">
                            <div className="actions_icon_wrapper">
                              {hasViewer && !!isViewType && (
                                <IconButton
                                  className="actions_icon"
                                  disabled={!isViewType || !!isModelFileLoadingProgress.value}
                                  onClick={() => {
                                    if (item.type === 'html') {
                                      return onClickImageFileLocal(_, item.id, {
                                        action: 'toggleViewLocalHtmlFile',
                                        value: {
                                          name: item.name,
                                          isView: item.isView,
                                        },
                                      });
                                    }
                                    if (imgTypes.includes(item.type)) {
                                      return onClickImageFileLocal(_, item.id, {
                                        action: 'toggleViewLocalImageFile',
                                        value: {
                                          isView: item.isView,
                                          name: item.name,
                                          size: item.size,
                                        },
                                      });
                                    }
                                    if (modelTypes.includes(item.type)) {
                                      return onClickViewModelFileLocal(_, item.id, {
                                        action: 'toggleViewLocalModelFile',
                                        value: {
                                          id: item.id,
                                          name: item.name,
                                          type: item.type,
                                          isView: item.isView,
                                          viewColor: item.viewColor,
                                          resourceFileList: item.resourceFileList,
                                          groupName: item.groupName,
                                        },
                                      });
                                    }
                                  }}
                                >
                                  {!!isModelFileLoadingProgress.value ? (
                                    item.id === selectedModelFile ? (
                                      <CircularLoading size={15} />
                                    ) : (
                                      <EyeIcon width={18} color={color.gray_deep_weak} />
                                    )
                                  ) : (
                                    <EyeIcon width={18} color={eyeIconColor} />
                                  )}
                                </IconButton>
                              )}
                            </div>

                            <div className="actions_icon_wrapper">
                              {!!isModelType && (
                                <IconButton
                                  className="actions_icon"
                                  disabled={!!isModelFileLoadingProgress.value}
                                  onClick={() => {
                                    onClickWireframe(item.id, {
                                      wireFrame: item.isWireframe,
                                    });
                                  }}
                                >
                                  {!item.isView || !item.isWireframe ? (
                                    <img src={icon_mesh_off} />
                                  ) : (
                                    <img src={icon_mesh_on} />
                                  )}
                                </IconButton>
                              )}
                            </div>
                            <div className="actions_icon_wrapper"></div>
                          </div>
                        </div>
                      );
                    })}

                  {!hasAlarm && !fileList.value?.length && (
                    <label
                      onClick={() => onClickFileImport()}
                      className="projectUpload__table_file_open_btn_box cursor-pointer"
                    >
                      <div className="projectUpload__table_file_open_in_box">
                        <img src={icon_folder_plus} alt="folder open" className="icon" />{' '}
                        <div className="projectUpload__table_file_open_info_box">
                          <span className="projectUpload__table_file_open_btn drag">
                            <T>GLOBAL_DRAG_DROP</T>
                            {', '}
                          </span>
                          <span className="projectUpload__table_file_open_btn click">
                            <T>GLOBAL_BROWSE_FILES</T>
                          </span>{' '}
                          <p className="projectUpload__table_file_open_notice">
                            <T>GLOBAL_FILE_UPLOAD_INFO</T>
                          </p>
                        </div>
                      </div>
                    </label>
                  )}
                </>
              ),
              [fileList.value, isModelFileLoadingProgress.value, isCheckAll],
            )}
          </div>
        </DropzoneWrapper>
        <input
          type="file"
          multiple
          name="localFileInput"
          id="localFileInput"
          ref={localFileInputRef}
          onChange={localFileInput.onChange}
          hidden
        />
      </div>
    </StyledProjectFileGroupLocal>
  );
}

const StyledProjectFileGroupLocal = styled.div<{}>`
  width: 100%;
  .projectFileGroupLocal__header {
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid #e1e1e7;
    border-bottom: 1px solid #e1e1e7;

    .checkBox {
      &:hover {
        cursor: pointer;
      }
    }
    .projectFileGroupLocal__title_box {
      padding-left: 15px;
      display: flex;
      column-gap: 15px;
      .projectFileGroupLocal__title {
        font-size: 14px;
        font-weight: 600;
        color: #25256f;
        display: flex;
        align-items: center;
      }
    }
    .projectFileGroupLocal__button_box {
      padding-right: 10px;
      .projectFileGroupLocal__button {
        display: flex;
        align-items: center;
        padding: 0;
        border-radius: 2px;
        font-size: 13px;
        font-weight: 400;
        color: #ced2df;
        &.on {
          color: #25256f;
        }
      }
    }
  }
  .projectFileGroupLocal__body {
    width: 400px;
    height: 172px;
    /* border-bottom: 1px solid #e1e1e7; */
    .projectFileGroupLocal__file_list {
      width: 400px;
      height: 172px;
      padding: 5px 0 0 10px;

      position: relative;
      overflow-y: scroll;
    }
  }

  .projectFileGroupLocal__file_row {
    background-color: #f9f9fb;
    width: 100%;
    border-radius: 10px;
    /* height: 42px; */
    display: flex;
    align-items: center;
    justify-content: flex-start;
    column-gap: 10px;
    margin-bottom: 5px;
    padding: 5px 10px;

    .projectFileGroupLocal__file_row_cell {
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;

      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      label {
        &.isHover {
          cursor: pointer;
        }
      }

      &.check_item {
      }

      &.filename {
        flex-direction: column;
        /* width: 148px; */
        width: 100%;
        .projectFileGroupLocal__file_list_item_text {
          display: inline-block;
          max-width: 160px;
          width: 100%;
          font-size: 13px;
          color: #666666;
          line-height: 18px;
        }
        .slider_box {
          /* width: 148px; */
          max-width: 160px;
          width: 100%;
          height: 15px;
          .MuiSlider-root {
            color: #25256f;
            height: 4px;
            padding: 0;
            .MuiSlider-rail {
              height: 4px;
              border-radius: 2px;
              background-image: linear-gradient(to bottom, #949494, #b1b1b1 52%, #d9d9d9);
            }
            .MuiSlider-track {
              height: 4px;
              border-radius: 2px;
            }
            .MuiSlider-valueLabel {
              left: calc(-50% - 2px);
            }
            .MuiSlider-thumb {
              height: 14px;
              width: 14px;
              background-color: transparent;
              background-image: url(${btn_opacity});
              background-size: cover;
              background-repeat: no-repeat;

              &:focus,
              &:hover,
              &:active {
                box-shadow: inherit;
              }
            }

            &.Mui-disabled {
              color: #25256f;
              height: 4px;
              padding: 0;
              opacity: 0.6;

              .MuiSlider-thumb {
                margin-top: -5px;
                margin-left: -6px;
              }
            }
          }
        }
      }
      &.type {
        .projectUpload__view_icon {
          color: ${color.blue};
          padding: 0;
        }
      }

      &.actions {
        column-gap: 13px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        .actions_icon_wrapper {
          width: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          .actions_icon {
            width: 24px;
            height: 24px;
            padding: 0;
          }
        }
      }
    }
  }

  .projectUpload__table_file_open_btn_box {
    .projectUpload__table_file_open_in_box {
      height: 162px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      .projectUpload__table_file_open_info_box {
        text-align: center;
        font-size: 13px;
        line-height: 18px;
      }
    }
  }
`;

export default React.memo(ProjectFileGroupLocal);

const FileSlider = withStyles({})(Slider);

interface ValueLabelComponentProps {
  children: any;
  open: boolean;
  value: number;
}
function ValueLabelComponent(props: ValueLabelComponentProps) {
  const { children, open, value } = props;
  const classes = useStyles();

  return (
    <Tooltip
      open={open}
      enterTouchDelay={0}
      placement="top"
      title={value}
      classes={{ tooltip: classes.tooltip }}
    >
      {children}
    </Tooltip>
  );
}

const useStyles = makeStyles({
  tooltip: {
    marginBottom: '4px',
  },
});
